import { Routes } from '@angular/router';
import { UnsavedChangesGuard } from './core/guards/unsaved-changes.guard';
import { RoleGuard } from './core/guards/role.guard';
import { AuthGuard } from './core/guards/auth.guard';
import { UnauthorizedAccessGuard } from './core/guards/unauthorized-access.guard';
import { ProfessionalProfileGuard } from './core/guards/professional-profile.guard';
import { PrivacyNoticeGuard } from './core/guards/privacy-notice.guard';
import { PreRegistrationGuard } from './core/guards/pre-registration.guard';
import { ReviewAccessGuard } from './core/guards/review-access.guard';

import { ListQuestionnairesComponent } from './components/list-questionnaires/list-questionnaires.component';
import { CreateQuestionnaireComponent } from './components/create-questionnaire/create-questionnaire.component';
import { QuestionnaireRunComponent } from './components/questionnaire-run/questionnaire-run.component';
import { QuestionnaireRunBoComponent } from './components/questionnaire-run-bo/questionnaire-run-bo.component';
import { ChecklistRunBoComponent } from './components/checklist-run-bo/checklist-run-bo.component';
import { ChecklistRunUserComponent } from './components/checklist-run-user/checklist-run-user.component';
import { ContractSectionsComponent } from './backoffice/components/contract-sections/contract-sections.component';
import { ContractSectionsFormComponent } from './backoffice/components/contract-sections-form/contract-sections-form.component';
import { ContractTemplatesComponent } from './backoffice/components/contract-templates/contract-templates';
import { TemplatesRegistryComponent } from './backoffice/components/templates-registry/templates-registry.component';
import { DocumentTemplateListComponent } from './backoffice/components/document-template-list/document-template-list.component';
import { DocumentAttachmentListComponent } from './backoffice/components/document-attachment-list/document-attachment-list.component';
import { DocumentArticleListComponent } from './backoffice/components/document-article-list/document-article-list.component';

import { ExpertsHomepage } from './frontoffice/components/experts-homepage/experts-homepage';
import { Dashboard } from './backoffice/components/dashboard/dashboard';
import { PageNotFoundComponent } from './core/shared/page-not-found/page-not-found.component';
import { UnauthorizedComponent } from './core/shared/unauthorized/unauthorized.component';
import { SessionExpiredComponent } from './core/shared/session-expired/session-expired.component';
import { ComingSoonComponent } from './core/shared/coming-soon/coming-soon.component';
import { LoginComponent } from './core/login/login';
import { AuthCallbackComponent } from './core/login/auth-callback.component';
import { AuthTestComponent } from './core/auth-test/auth-test.component';
import { PrivacyNoticeComponent } from './core/privacy-notice/privacy-notice.component';
import { HomeComponent } from './backoffice/components/home/home';
import { SuppliersRegistryComponent } from './backoffice/components/suppliers-registry/suppliers-registry.component';
import { SupplierFormComponent } from './backoffice/components/suppliers-registry/supplier-form/supplier-form.component';
import { ProfessionalsRegistryComponent } from './backoffice/components/professionals-registry/professionals-registry.component';
import { ReviewProfessionalComponent } from './backoffice/components/review-professional/review-professional.component';
import { ResponsibleFormComponent } from './backoffice/components/responsibles-registry/responsible-form/responsible-form.component';
import { ResponsiblesRegistryComponent } from './backoffice/components/responsibles-registry/responsibles-registry.component';
import { SubResponsiblesRegistryComponent } from './backoffice/components/sub-responsibles-registry/sub-responsibles-registry.component';
import { SubResponsibleFormComponent } from './backoffice/components/sub-responsibles-registry/sub-responsible-form/sub-responsible-form.component';
import { SubResponsibleDetailModalComponent } from './backoffice/components/sub-responsibles-registry/sub-responsible-detail/sub-responsible-detail.component';
import { SupplierDetailModalComponent } from './backoffice/components/suppliers-registry/supplier-detail/supplier-detail.component';
import { ResponsibleDetailModalComponent } from './backoffice/components/responsibles-registry/responsible-detail/responsible-detail.component';
import { ContractsRegistryComponent } from './backoffice/components/contracts-registry/contracts-registry.component';
import { ContractDetailComponent } from './backoffice/components/contracts-registry/contract-detail/contract-detail.component';
import { ContractFormComponent } from './backoffice/components/contracts-registry/contract-form/contract-form.component';
import { ContractActionFormComponent } from './backoffice/components/contracts-registry/contract-action-form/contract-action-form.component';
import { ContractActionDetailComponent } from './backoffice/components/contracts-registry/contract-action-detail/contract-action-detail.component';
import { PreRegistrationComponent } from './core/pre-registration/pre-registration.component';
import { ProfessionalsProgressionComponent } from './frontoffice/components/professionals-progression/professionals-progression.component';
import { ProfessionalsMfaComponent } from './frontoffice/components/professionals-mfa/professionals-mfa.component';
import { QuestionnairesAttachmentsComponent } from './core/questionnaires-attachments/questionnaires-attachments.component';
import { AttachmentConfigComponent } from './core/questionnaires-attachments/attachment-config/attachment-config.component';
import { CreateFileChecklist } from './components/create-file-checklist/create-file-checklist';
import { ListChecklistComponent } from './components/list-checklist/list-checklist.component';
import { ProfessionalsCollaboratorsComponent } from './frontoffice/components/professionals-personal-info/professionals-collaborators/professionals-collaborators.component';
import { ProfessionalsGeneralDataComponent } from './frontoffice/components/professionals-personal-info/professionals-general-data/professionals-general-data.component';
import { ProfessionalsPersonalInfoComponent } from './frontoffice/components/professionals-personal-info/professionals-personal-info.component';
import { ProfessionalsQuestionnaireAttachmentsComponent } from './frontoffice/components/professionals-personal-info/professionals-questionnaire-attachments/professionals-questionnaire-attachments.component';
import { ProfessionalsSpecificDataComponent } from './frontoffice/components/professionals-personal-info/professionals-specific-data/professionals-specific-data.component';
import { VrfQuestionnaireComponent } from './frontoffice/components/professionals-personal-info/professionals-questionnaire-attachments/vrf-questionnaire/vrf-questionnaire.component';
import { ProfessionalsContractSignatureComponent } from './frontoffice/components/professionals-personal-info/professionals-contract-signature/professionals-contract-signature.component';
import { NetworkDetailComponent } from './backoffice/components/network-detail/network-detail.component';
import { PaymentRateComponent } from './components/payment-rate/payment-rate';
import { SuppliersDashboardComponent } from './backoffice/components/suppliers-dashboard/suppliers-dashboard';
import { AccessibilityDeclarationComponent } from './core/shared/accessibility-declaration/accessibility-declaration.component';
import { BuildInfoComponent } from './core/shared/build-info/build-info.component';
import { AlertsRegistryComponent } from './backoffice/components/alerts-registry/alerts-registry.component';

export const routes: Routes = [
  { path: '', redirectTo: 'login', pathMatch: 'full' }, // ✅ Redirect default path to login
  { path: 'login', component: LoginComponent }, // ✅ Azure AD Login (production ready)
  { path: 'auth/callback', component: AuthCallbackComponent }, // ✅ OAuth callback handler
  { path: 'auth-test', component: AuthTestComponent }, // 🔧 DEV ONLY - Authentication testing page
  { path: 'unauthorized', component: UnauthorizedComponent }, // ✅ Accessible when redirected by guards
  { path: 'session-expired', component: SessionExpiredComponent }, // ⏰ Shown when session expired and silent refresh failed
  { path: 'home', component: HomeComponent },
  { path: 'experts', component: ExpertsHomepage },
  { path: 'contract-sections', component: ContractSectionsComponent },
  { path: 'payment-rates', component: PaymentRateComponent },
  // ✔️ Legacy redirect: /axa/dashboard → /axa/professionals-dashboard
  { path: 'axa/dashboard', redirectTo: 'axa/professionals-dashboard', pathMatch: 'full' },
  {
    path: 'axa/professionals-dashboard',
    component: Dashboard,
    canActivate: [AuthGuard],
    data: {
      roles: ['admin', 'backoffice_admin', 'backoffice_professional', 'backoffice_supplier'],
    },
  },
  {
    path: 'axa/suppliers-dashboard',
    component: SuppliersDashboardComponent,
    canActivate: [AuthGuard],
    data: { roles: ['admin', 'backoffice_admin', 'backoffice_supplier'] },
  },
  {
    path: 'axa/suppliers-registry',
    component: SuppliersRegistryComponent,
    canActivate: [AuthGuard],
    data: { roles: ['admin', 'backoffice_admin', 'backoffice_supplier'] },
  },
  {
    path: 'axa/suppliers-registry-sub-fornitori',
    component: ComingSoonComponent,
    canActivate: [AuthGuard],
    data: { roles: ['admin', 'backoffice_admin', 'backoffice_supplier'] },
  },
  {
    path: 'axa/supplier/:id',
    component: SupplierDetailModalComponent,
    canActivate: [AuthGuard],
    canDeactivate: [UnsavedChangesGuard],
    data: { roles: ['admin', 'backoffice_admin', 'backoffice_supplier'] },
  },
  {
    path: 'axa/alerts',
    component: AlertsRegistryComponent,
    canActivate: [AuthGuard],
    data: { roles: ['admin', 'backoffice_admin', 'backoffice_professional'] },
  },
  {
    path: 'axa/professionals-registry',
    component: ProfessionalsRegistryComponent,
    canActivate: [AuthGuard],
    data: {
      roles: ['admin', 'backoffice_admin', 'backoffice_professional' ],
    },
  },
  {
    path: 'axa/professionals-registry/:id',
    component: ReviewProfessionalComponent,
    canActivate: [AuthGuard],
    data: {
      roles: ['admin', 'backoffice_admin', 'backoffice_professional'],
      mode: 'detail',
    },
  },
  {
    path: 'axa/review-professional/:id',
    component: ReviewProfessionalComponent,
    canActivate: [AuthGuard, ReviewAccessGuard],
  },
  {
    path: 'axa/responsibles-registry',
    component: ResponsiblesRegistryComponent,
    canActivate: [AuthGuard],
    data: {
      roles: ['admin', 'backoffice_admin', 'backoffice_supplier'],
    },
  },
  {
    path: 'axa/responsible/:id',
    component: ResponsibleDetailModalComponent,
    canActivate: [AuthGuard],
    data: {
      roles: ['admin', 'backoffice_admin', 'backoffice_supplier'],
    },
  },
  {
    path: 'axa/sub-responsibles-registry',
    component: SubResponsiblesRegistryComponent,
    canActivate: [AuthGuard],
    data: {
      roles: ['admin', 'backoffice_admin', 'backoffice_supplier'],
    },
  },
  {
    path: 'axa/sub-responsible/:id',
    component: SubResponsibleDetailModalComponent,
    canActivate: [AuthGuard],
    data: {
      roles: ['admin', 'backoffice_admin', 'backoffice_supplier'],
    },
  },
  {
    path: 'axa/supplier-registration-wizard',
    component: SupplierFormComponent,
    canActivate: [AuthGuard],
    data: {
      roles: ['admin', 'backoffice_admin', 'backoffice_supplier'],
    },
  },
  {
    path: 'axa/supplier-registration-wizard/:id',
    component: SupplierFormComponent,
    canActivate: [AuthGuard],
    data: {
      roles: ['admin', 'backoffice_admin', 'backoffice_supplier'],
    },
  },
  {
    path: 'axa/responsible-registration-wizard',
    component: ResponsibleFormComponent,
    canActivate: [AuthGuard],
    data: {
      roles: ['admin', 'backoffice_admin', 'backoffice_supplier'],
    },
  },
  {
    path: 'axa/sub-responsible-registration-wizard',
    component: SubResponsibleFormComponent,
    canActivate: [AuthGuard],
    data: {
      roles: ['admin', 'backoffice_admin', 'backoffice_supplier'],
    },
  },
  // ─── ROUTE DOMINIO FORNITORI ──────────────────────────────────────────────
  { path: 'axa/supplier-contracts-registry', component: ContractsRegistryComponent, canActivate: [AuthGuard], data: { domain: 'SUPPLIERS', roles: ['admin', 'backoffice_admin', 'backoffice_supplier'] } },
  { path: 'axa/supplier-contracts-registry/:id', component: ContractDetailComponent, canActivate: [AuthGuard], data: { domain: 'SUPPLIERS', roles: ['admin', 'backoffice_admin', 'backoffice_supplier'] } },
  { path: 'axa/supplier-contracts-registry/:contractId/actions/new', component: ContractActionFormComponent, canActivate: [AuthGuard], data: { domain: 'SUPPLIERS', roles: ['admin', 'backoffice_admin', 'backoffice_supplier'] } },
  { path: 'axa/supplier-contracts-registry/:contractId/actions/:actionId/edit', component: ContractActionFormComponent, canActivate: [AuthGuard], data: { domain: 'SUPPLIERS', roles: ['admin', 'backoffice_admin', 'backoffice_supplier'] } },
  { path: 'axa/supplier-contracts-registry/:contractId/actions/:actionId', component: ContractActionDetailComponent, canActivate: [AuthGuard], canDeactivate: [UnsavedChangesGuard], data: { domain: 'SUPPLIERS', roles: ['admin', 'backoffice_admin', 'backoffice_supplier'] } },
  { path: 'axa/supplier-templates-registry', component: TemplatesRegistryComponent, canActivate: [AuthGuard], data: { domain: 'SUPPLIERS', roles: ['admin', 'backoffice_admin', 'backoffice_supplier'] } },
  { path: 'axa/supplier-document-templates', component: DocumentTemplateListComponent, canActivate: [AuthGuard], data: { domain: 'SUPPLIERS', roles: ['admin', 'backoffice_admin', 'backoffice_supplier'] } },
  { path: 'axa/supplier-document-attachments', component: DocumentAttachmentListComponent, canActivate: [AuthGuard], data: { domain: 'SUPPLIERS', roles: ['admin', 'backoffice_admin', 'backoffice_supplier'] } },
  { path: 'axa/supplier-document-articles', component: DocumentArticleListComponent, canActivate: [AuthGuard], data: { domain: 'SUPPLIERS', roles: ['admin', 'backoffice_admin', 'backoffice_supplier'] } },
  { path: 'axa/supplier-contract-templates', component: ContractTemplatesComponent, canActivate: [AuthGuard], canDeactivate: [UnsavedChangesGuard], data: { domain: 'SUPPLIERS', roles: ['admin', 'backoffice_admin', 'backoffice_supplier'] } },
  { path: 'axa/supplier-contract-sections', component: ContractSectionsComponent, canActivate: [AuthGuard], data: { domain: 'SUPPLIERS', roles: ['admin', 'backoffice_admin', 'backoffice_supplier'] } },
  { path: 'axa/supplier-contract-sections/new', component: ContractSectionsFormComponent, canActivate: [AuthGuard], canDeactivate: [UnsavedChangesGuard], data: { domain: 'SUPPLIERS', roles: ['admin', 'backoffice_admin', 'backoffice_supplier'] } },
  { path: 'axa/supplier-contract-sections/edit/:id', component: ContractSectionsFormComponent, canActivate: [AuthGuard], canDeactivate: [UnsavedChangesGuard], data: { domain: 'SUPPLIERS', roles: ['admin', 'backoffice_admin', 'backoffice_supplier'] } },
  { path: 'axa/supplier-contract-registration-wizard', component: ContractFormComponent, canActivate: [AuthGuard], canDeactivate: [UnsavedChangesGuard], data: { domain: 'SUPPLIERS', roles: ['admin', 'backoffice_admin', 'backoffice_supplier'] } },
  { path: 'axa/supplier-contract-registration-wizard/:id', component: ContractFormComponent, canActivate: [AuthGuard], canDeactivate: [UnsavedChangesGuard], data: { domain: 'SUPPLIERS', roles: ['admin', 'backoffice_admin', 'backoffice_supplier'] } },

  // ─── ROUTE DOMINIO FIDUCIARI ──────────────────────────────────────────────
  { path: 'axa/professional-contracts-registry', component: ContractsRegistryComponent, canActivate: [AuthGuard], data: { domain: 'PROFESSIONALS', roles: ['admin', 'backoffice_admin', 'backoffice_professional'] } },
  { path: 'axa/professional-contracts-registry/:id', component: ContractDetailComponent, canActivate: [AuthGuard], data: { domain: 'PROFESSIONALS', roles: ['admin', 'backoffice_admin', 'backoffice_professional'] } },
  { path: 'axa/professional-contracts-registry/:contractId/actions/new', component: ContractActionFormComponent, canActivate: [AuthGuard], data: { domain: 'PROFESSIONALS', roles: ['admin', 'backoffice_admin', 'backoffice_professional'] } },
  { path: 'axa/professional-contracts-registry/:contractId/actions/:actionId/edit', component: ContractActionFormComponent, canActivate: [AuthGuard], data: { domain: 'PROFESSIONALS', roles: ['admin', 'backoffice_admin', 'backoffice_professional'] } },
  { path: 'axa/professional-contracts-registry/:contractId/actions/:actionId', component: ContractActionDetailComponent, canActivate: [AuthGuard], canDeactivate: [UnsavedChangesGuard], data: { domain: 'PROFESSIONALS', roles: ['admin', 'backoffice_admin', 'backoffice_professional'] } },
  { path: 'axa/professional-templates-registry', component: TemplatesRegistryComponent, canActivate: [AuthGuard], data: { domain: 'PROFESSIONALS', roles: ['admin', 'backoffice_admin', 'backoffice_professional'] } },
  { path: 'axa/professional-document-templates', component: DocumentTemplateListComponent, canActivate: [AuthGuard], data: { domain: 'PROFESSIONALS', roles: ['admin', 'backoffice_admin', 'backoffice_professional'] } },
  { path: 'axa/professional-document-attachments', component: DocumentAttachmentListComponent, canActivate: [AuthGuard], data: { domain: 'PROFESSIONALS', roles: ['admin', 'backoffice_admin', 'backoffice_professional'] } },
  { path: 'axa/professional-document-articles', component: DocumentArticleListComponent, canActivate: [AuthGuard], data: { domain: 'PROFESSIONALS', roles: ['admin', 'backoffice_admin', 'backoffice_professional'] } },
  { path: 'axa/professional-contract-templates', component: ContractTemplatesComponent, canActivate: [AuthGuard], canDeactivate: [UnsavedChangesGuard], data: { domain: 'PROFESSIONALS', roles: ['admin', 'backoffice_admin', 'backoffice_professional'] } },
  { path: 'axa/professional-contract-sections', component: ContractSectionsComponent, canActivate: [AuthGuard], data: { domain: 'PROFESSIONALS', roles: ['admin', 'backoffice_admin', 'backoffice_professional'] } },
  { path: 'axa/professional-contract-sections/new', component: ContractSectionsFormComponent, canActivate: [AuthGuard], canDeactivate: [UnsavedChangesGuard], data: { domain: 'PROFESSIONALS', roles: ['admin', 'backoffice_admin', 'backoffice_professional'] } },
  { path: 'axa/professional-contract-sections/edit/:id', component: ContractSectionsFormComponent, canActivate: [AuthGuard], canDeactivate: [UnsavedChangesGuard], data: { domain: 'PROFESSIONALS', roles: ['admin', 'backoffice_admin', 'backoffice_professional'] } },
  { path: 'axa/professional-contract-registration-wizard', component: ContractFormComponent, canActivate: [AuthGuard], canDeactivate: [UnsavedChangesGuard], data: { domain: 'PROFESSIONALS', roles: ['admin', 'backoffice_admin', 'backoffice_professional'] } },
  { path: 'axa/professional-contract-registration-wizard/:id', component: ContractFormComponent, canActivate: [AuthGuard], canDeactivate: [UnsavedChangesGuard], data: { domain: 'PROFESSIONALS', roles: ['admin', 'backoffice_admin', 'backoffice_professional'] } },

  {
    path: 'axa/pre-registration',
    component: PreRegistrationComponent,
    canActivate: [AuthGuard, PrivacyNoticeGuard, PreRegistrationGuard],
    data: { roles: ['admin', 'backoffice_admin', 'backoffice_professional', 'professional'] },
  },
  {
    path: 'axa/professionals-privacy-notice',
    component: PrivacyNoticeComponent,
    canActivate: [AuthGuard],
    data: { roles: ['professional', 'collaborator'] },
  },
  {
    path: 'axa/professionals-registration-progress',
    component: ProfessionalsProgressionComponent,
    canActivate: [AuthGuard, ProfessionalProfileGuard, PrivacyNoticeGuard],
    data: { roles: ['professional', 'collaborator'] },
  },
  {
    // Wizard MFA (Codici SOL) — compilazione a carico del fiduciario dopo l'approvazione.
    path: 'axa/professionals-mfa',
    component: ProfessionalsMfaComponent,
    canActivate: [AuthGuard, ProfessionalProfileGuard, PrivacyNoticeGuard],
    data: { roles: ['professional', 'collaborator'] },
  },
  {
    path: 'axa/professionals-personal-info',
    component: ProfessionalsPersonalInfoComponent,
    canActivate: [AuthGuard, ProfessionalProfileGuard, PrivacyNoticeGuard],
    data: {
      roles: [
        'admin',
        'backoffice_admin',
        'backoffice_professional',
        'professional',
        'collaborator',
      ],
    },
  },
  {
    path: 'axa/professionals-general-data',
    component: ProfessionalsGeneralDataComponent,
    canActivate: [AuthGuard, ProfessionalProfileGuard, PrivacyNoticeGuard],
    canDeactivate: [UnsavedChangesGuard],
    data: {
      roles: [
        'admin',
        'professional',
        'collaborator',
      ],
    },
  },
  {
    path: 'axa/professionals-specific-data',
    component: ProfessionalsSpecificDataComponent,
    canActivate: [AuthGuard, ProfessionalProfileGuard, PrivacyNoticeGuard],
    canDeactivate: [UnsavedChangesGuard],
    data: {
      roles: [
        'admin',
        'professional',
        'backoffice_professional',
        'backoffice_admin',
        'collaborator',
      ],
    },
  },
  {
    // NK_BODYSHOP onboarding route — BO only, ID passed explicitly in path
    path: 'axa/professionals-specific-data/:id',
    component: ProfessionalsSpecificDataComponent,
    canActivate: [AuthGuard],
    canDeactivate: [UnsavedChangesGuard],
    data: {
      roles: ['admin', 'backoffice_admin', 'backoffice_professional'],
    },
  },
  {
    path: 'axa/professionals-staff-collaborators',
    component: ProfessionalsCollaboratorsComponent,
    canActivate: [AuthGuard, ProfessionalProfileGuard, PrivacyNoticeGuard],
    canDeactivate: [UnsavedChangesGuard],
    data: {
      roles: [
        'admin',
        'professional',
        'collaborator',
        'backoffice_admin',
        'backoffice_professional',
      ],
    },
  },
  {
    // Legacy path kept for backward compatibility – redirects to the unified section.
    path: 'axa/professionals-administrative-staff',
    redirectTo: 'axa/professionals-staff-collaborators',
    pathMatch: 'full',
  },
  {
    // Legacy collaborators-only path – collaborators are now part of the unified section.
    path: 'axa/professionals-collaborators',
    redirectTo: 'axa/professionals-staff-collaborators',
    pathMatch: 'full',
  },
  {
    path: 'axa/professionals-questionnaire-attachments',
    component: ProfessionalsQuestionnaireAttachmentsComponent,
    canActivate: [AuthGuard, ProfessionalProfileGuard, PrivacyNoticeGuard],
    data: {
      roles: [
        'admin',
        'professional',
        'collaborator',
      ],
    },
  },
  {
    // NK_BODYSHOP onboarding route — BO only, ID passed explicitly in path
    path: 'axa/professionals-questionnaire-attachments/:id',
    component: ProfessionalsQuestionnaireAttachmentsComponent,
    canActivate: [AuthGuard],
    data: {
      roles: ['admin', 'backoffice_admin', 'backoffice_professional'],
    },
  },
  {
    path: 'axa/vrf-questionnaire',
    component: VrfQuestionnaireComponent,
    canActivate: [AuthGuard, ProfessionalProfileGuard, PrivacyNoticeGuard],
    canDeactivate: [UnsavedChangesGuard],
    data: { roles: ['admin', 'backoffice_admin', 'backoffice_professional', 'professional', 'collaborator'] },
  },
  {
    path: 'axa/vrf-questionnaire/:id',
    component: VrfQuestionnaireComponent,
    canActivate: [AuthGuard, ProfessionalProfileGuard, PrivacyNoticeGuard],
    canDeactivate: [UnsavedChangesGuard],
    data: { roles: ['admin', 'backoffice_admin', 'backoffice_professional', 'professional', 'collaborator'] },
  },
  {
    path: 'axa/professionals-contracts',
    component: ProfessionalsContractSignatureComponent,
    canActivate: [AuthGuard, ProfessionalProfileGuard, PrivacyNoticeGuard],
    data: {
      roles: [
        'admin',
        'backoffice_admin',
        'backoffice_professional',
        'professional',
        'collaborator',
      ],
    },
  },
  {
    path: 'axa/network-detail/:id',
    component: NetworkDetailComponent,
    canActivate: [AuthGuard],
    data: { roles: ['admin', 'backoffice_admin', 'backoffice_professional'] },
  },
  {
    path: 'axa/questionnaires-and-attachments',
    component: QuestionnairesAttachmentsComponent,
    canActivate: [AuthGuard],
    data: { roles: ['admin', 'backoffice_admin', 'backoffice_professional'] },
  },
  {
    path: 'axa/attachment-config',
    component: AttachmentConfigComponent,
    canActivate: [AuthGuard],
    data: { roles: ['admin', 'backoffice_admin', 'backoffice_professional'] },
  },
  { path: 'questionnaires', 
    component: ListQuestionnairesComponent,
    canActivate: [AuthGuard],
    data: {
      roles: ['admin', 'backoffice_admin', 'backoffice_professional'],
    },
  },
  { path: 'questionnaires/:id/run', 
    component: QuestionnaireRunComponent,
    canActivate: [AuthGuard],
    data: {
      roles: ['admin', 'backoffice_admin', 'backoffice_professional', 'professional'],
    }, 
  },
  {
    path: 'axa/questionnaires/:id/run',
    component: QuestionnaireRunBoComponent,
    canActivate: [AuthGuard],
    data: {
      roles: ['admin', 'backoffice_admin', 'backoffice_professional', 'backoffice_supplier'],
    },
  },
  {
    path: 'axa/file-checklist/:id/run',
    component: ChecklistRunBoComponent,
    canActivate: [AuthGuard],
    data: {
      roles: ['admin', 'backoffice_admin', 'backoffice_professional', 'backoffice_supplier'],
    },
  },
  {
    path: 'axa/file-checklist/:id/run-user',
    component: ChecklistRunUserComponent,
    canActivate: [AuthGuard],
    data: {
      roles: ['admin', 'backoffice_admin', 'backoffice_professional', 'backoffice_supplier'],
    },
  },
  { path: 'create-questionnaire', 
    component: CreateQuestionnaireComponent, 
    canActivate: [AuthGuard],
    data: {
      roles: ['admin', 'backoffice_admin', 'backoffice_professional'],
    },
  },
  { path: 'create-questionnaire/:id', 
    component: CreateQuestionnaireComponent, 
    canActivate: [AuthGuard],
    data: {
      roles: ['admin', 'backoffice_admin', 'backoffice_professional'],
    },
  },
  { path: 'list-file-checklist', 
    component: ListChecklistComponent,
    canActivate: [AuthGuard],
    data: {
      roles: ['admin', 'backoffice_admin', 'backoffice_professional'],
    },
  },
  { path: 'create-file-checklist', 
    component: CreateFileChecklist,
    canActivate: [AuthGuard],
    data: {
      roles: ['admin', 'backoffice_admin', 'backoffice_professional'],
    },
  },
  { path: 'create-file-checklist/:id', 
    component: CreateFileChecklist,
    canActivate: [AuthGuard],
    data: {
      roles: ['admin', 'backoffice_admin', 'backoffice_professional'],
    },
  },
  { path: 'accessibilita', component: AccessibilityDeclarationComponent }, // ✅ Dichiarazione di accessibilità (pubblica, no auth)
  {
    path: 'informativa-privacy',
    component: PrivacyNoticeComponent,
    canActivate: [AuthGuard],
    data: {
      publicView: true,
      roles: ['professional', 'collaborator'],
    },
  },
  { path: '__build-info', component: BuildInfoComponent },
  { path: '404', component: PageNotFoundComponent }, //explicit route
  { path: '**', component: PageNotFoundComponent }, //ALWAYS LAST
];
